<template>
    <form
        @submit.prevent="handleSubmit"
        :data-uuid="settings.uuid"
        class="custom-block"
        :style="blockStyle"
        ref="form"
        action=""
        method="POST"
    >
        <template v-if="loading || settings.general.loaderVisible">
            <SpinLoader :color="general.loader || '#3965ff'" :style="loaderStyle" />
            <component :is="'style'">
                {{ loaderInnerStyle }}
            </component>
        </template>

        <WidgetBlockContainer
            :style="stepStyle"
            :settings="settings.children.find(child => child.isActive)"
            :blocks-map="blocksMap"
            :parent="settings"
            :elements-map="elementsMap"
        />

        <div v-if="message && !errors.length" :style="messageStyle">
            {{ message }}
        </div>
    </form>
</template>

<script>
import ViewMixin from "../utils/view-mixin"
import {
    allEvents,
    noneEvents,
    resolveBackground,
    resolveMargin,
    resolvePadding,
    resolveShadow,
    typedWidth
} from "../utils/style"
import WidgetBlockContainer from "../blocks/WidgetBlockContainer.vue"
import SpinLoader from "../../../Loaders/SpinLoader.vue"
import { EventBus } from "../../../../utils/Events/index"
import { Debouncer } from "../../../../utils/Helpers/index"

const loaderStyle = `.spin-loader:before{content:"";display:block;padding-top:100%}.spin-loader .circular{animation:2s linear infinite rotate;height:100%;transform-origin:center center;width:100%;position:absolute;top:0;bottom:0;left:0;right:0;margin:auto}.spin-loader .path{stroke-dasharray:1,200;stroke-dashoffset:0;animation:1.5s ease-in-out infinite dash,6s ease-in-out infinite color;stroke-linecap:round}@keyframes rotate{100%{transform:rotate(360deg)}}@keyframes dash{0%{stroke-dasharray:1,200;stroke-dashoffset:0}50%{stroke-dasharray:89,200;stroke-dashoffset:-35px}100%{stroke-dasharray:89,200;stroke-dashoffset:-124px}}`

const TARGETS = {
    CONTACT: "contact",
    PRODUCT: "product",
    WEBHOOK: "webhook"
}

const APP_DOMAIN =
    window.app_domain === "kwiga.local"
        ? "http://kwiga.local"
        : window.app_domain === "kwiga3.link"
        ? "https://kwiga3.link"
        : "https://kwiga.com"

const PROFILE_URL = `${APP_DOMAIN}/profile/gate`

const FORM_ENDPOINT = `${APP_DOMAIN}/api/v1/cta-form`

const OFFER_ENDPOINT = `${APP_DOMAIN}/api/v1/cta-offer`

const UTM_MARKS = ["utm_source", "utm_campaign", "utm_medium", "utm_term", "utm_content"]

export default {
    components: { SpinLoader, WidgetBlockContainer },
    props: {
        settings: {
            type: Object,
            default: () => {}
        },
        elementsMap: {
            type: Object,
            default: () => {}
        },
        blocksMap: {
            type: Object,
            default: () => {}
        }
    },
    inject: ["uuid"],
    mixins: [ViewMixin],
    data() {
        return {
            message: "",
            errors: [],
            loading: false,
            visible: true,
            resolved: false,
            debouncer: new Debouncer(),
            isInFrame: false
        }
    },
    name: "WidgetForm",
    async mounted() {
        this.isInIframe = window.self !== window.top

        EventBus.$on(`update-offer:${this.settings.uuid}`, () => {
            this.debouncer.exec(() => {
                this.checkOfferData()
            }, 300)
        })

        if (!window.builder_dev_mode) {
            this.settings.children[0].isActive = true
            this.settings.children[1].isActive = false
        }
        if (window.builder_dev_mode) {
            this.debouncer.exec(() => {
                this.checkOfferData()
            }, 300)
        } else {
            this.checkOfferData()
        }
    },
    methods: {
        isRedirectInNewTab() {
            return false
        },
        redirect(url) {
            if (this.isRedirectInNewTab()) {
                window.open(url, "_blank")
            } else {
                window.top.location.href = url
            }
        },
        getLanguage() {
            let locale = "en"
            if (this.getLocale) {
                locale = this.getLocale()
            } else if (window.widgetLocale) {
                locale = window.widgetLocale
            } else if (window.app_current_locale) {
                locale = window.app_current_locale
            }

            return locale
        },
        async checkOfferData() {
            if (!this.payload.offers || !this.payload.offers.length) {
                return
            }

            const [offer] = this.payload.offers
            const response = await fetch(`${OFFER_ENDPOINT}/${offer.code}`, {
                headers: { "X-Language": this.getLanguage() }
            })
            const { data } = await response.json()

            this.settings.general.payload = {
                ...this.settings.general.payload,
                offer_data: data
            }

            const { merchant_settings, merchant_settings_count, payment_type } = data

            this.payload.offers[0] = {
                ...this.payload.offers[0],
                merchant_settings,
                merchant_settings_count,
                payment_type
            }
        },
        getPrependedUtm() {
            if (!import.meta?.url) {
                return {}
            }

            const params = new URL(import.meta.url).searchParams

            if (!params.has("utm")) {
                return {}
            } else {
                const marks = {}
                params
                    .get("utm")
                    .split(";")
                    .forEach(utm => {
                        const [mark, value] = utm.split("=")
                        marks[mark] = value
                    })
                return marks
            }
        },
        appendUtm(formData) {
            const urlParams = new URLSearchParams(
                this.isInFrame ? window?.parent?.location?.search : window?.location?.search
            )
            const marks = {}

            UTM_MARKS.forEach(mark => {
                if (urlParams.has(mark)) {
                    marks[mark] = urlParams.get(mark)
                }
            })

            for (const [utm, value] of Object.entries(marks)) {
                formData.append(utm, value)
            }

            for (const [utm, value] of Object.entries(this.getPrependedUtm())) {
                formData.append(utm, value)
            }

            for (const [key, value] of Object.entries(import.meta)) {
                if (key.includes("utm")) {
                    if (formData.has(key)) {
                        formData.delete(key)
                    }
                    formData.append(key, value)
                }
            }
        },
        async handleSubmit() {
            if (this.resolved) {
                this.hide()
                return
            }

            const fbqTrack = (result) => {
                if (window.fbq) {
                    if (result.fb_settings) {
                        if (result.fb_settings.key) {
                            let eventId = {eventID: result.fb_settings.event_id}
                            let eventData = result.fb_settings.event_data || {}

                            console.log(result.fb_settings.key, result.fb_settings.value, eventData, eventId)
                            window.fbq(result.fb_settings.key, result.fb_settings.value, eventData, eventId)
                        }
                    } else {
                        console.log(`fbqTrack`)
                        window.fbq("track", "Lead")
                    }
                }
            }

            this.message = ""
            this.errors = []

            const formData = new FormData(this.$refs.form)

            formData.append("cta_token", await this.injectToken())

            if (window.id_hash) {
                formData.append("user_id_hash", window.id_hash)
                formData.append("user_id", window.user_id)
            }

            if (document?.referrer) {
                formData.append("widget_landing_referer", document.referrer)
            }

            if (document?.URL) {
                formData.append("widget_landing", document.URL)
            }

            if (this.isInFrame) {
                formData.append("widget_landing", window?.parent?.location?.href)
            }

            if (!window.current_cabinet_id) {
                const visitor_hash = localStorage.getItem("kwgVisitor")
                const visits = localStorage.getItem("kwgVisits")

                if (visitor_hash && visits) {
                    formData.append("landing_visitor_hash", visitor_hash)
                    formData.append("landing_visits", visits)
                }
            }

            this.appendUtm(formData)

            if (this.general?.fb_settings) {
                function getCookieValue(name) {
                    const value = document.cookie
                        .split('; ')
                        .find(row => row.startsWith(name + '='))
                    return value ? value.split('=')[1] : null
                }

                this.general.fb_settings.fbc = getCookieValue('_fbc')
                this.general.fb_settings.fbp = getCookieValue('_fbp')

                const params = new URLSearchParams(window.location.search);

                this.general.fb_settings.fbclid = params.get('fbclid')

                formData.append("fb_settings", JSON.stringify(this.general.fb_settings))
            }

            const appUrl = await this.injectAppUrl()

            const url = `${FORM_ENDPOINT.replaceAll(APP_DOMAIN, appUrl)}/${this.uuid}/${this.target}`

            let callback = () => {}
            const handleResult = async result =>
                new Promise((resolve, reject) => {
                    if (
                        (result.hasOwnProperty("success") && result.success === "false" && result.message) ||
                        result.errors
                    ) {
                        reject([result.message, result.errors])
                    } else {
                        resolve()
                    }
                })

            const { tags } = this.settings.general

            if (tags) {
                const tagNames = tags.reduce((accumulator, current) => {
                    accumulator.push(current.name)
                    return accumulator
                }, [])

                if (tagNames && tagNames.length) {
                    formData.append("tags", tagNames)
                }
            }

            if (this.payload.redirect && this.payload.url) {
                formData.append("redirect_url", this.payload.url)
            }

            if (this.target === TARGETS.CONTACT) {
                callback = result => {
                    handleResult(result)
                        .then(() => {
                            this.message = ""
                            this.errors = []
                            this.resolved = true

                            fbqTrack(result)

                            const { redirect_url } = result
                            const { redirect } = this.payload

                            if (!this.payload.hasOwnProperty("redirect") && redirect_url) {
                                this.redirect(redirect_url)

                                return
                            }

                            if (redirect) {
                                this.redirect(redirect_url || PROFILE_URL)
                            } else {
                                this.settings.children[0].isActive = false
                                this.settings.children[1].isActive = true
                                this.$forceUpdate()
                            }
                        })
                        .catch(([message, errors]) => {
                            this.message = message
                            this.errors = errors
                        })
                }
            }

            if (this.target === TARGETS.PRODUCT) {
                const {
                    offers,
                    product,
                    send_activation_email,
                    notificationsData,
                    has_full_access_to_product,
                    redirect_payment_system,
                    payment_type,
                    merchant_setting,
                    processing_type
                } = this.payload

                formData.append("has_full_access_to_product", !!has_full_access_to_product)

                if (has_full_access_to_product) {
                    if (product) {
                        formData.append("product_id", product.id)
                    }
                } else if (offers.length) {
                    const [offer] = offers
                    formData.append("offer_id", offer.id)

                    if (this.isCanRedirectToPaymentSystem()) {
                        formData.append("redirect_payment_system", !!redirect_payment_system)
                    }

                    if (this.isCanPaymentProcessing()) {
                        formData.append("payment_processing_type", processing_type.value)

                        if (this.isPredefinedProcessing()) {
                            formData.append("merchant_setting_id", merchant_setting.id)
                            formData.append("payment_type", payment_type.value)

                            if (formData.has("redirect_payment_system")) {
                                formData.delete("redirect_payment_system")
                            }
                            formData.append("redirect_payment_system", true)
                        }
                    }
                }

                if (notificationsData) {
                    for (const notificationsDataKey in notificationsData) {
                        formData.append(notificationsDataKey, notificationsData[notificationsDataKey])
                    }
                } else {
                    formData.append("send_activation_email", !!send_activation_email)
                }

                callback = result => {
                    handleResult(result)
                        .then(() => {
                            this.message = ""
                            this.errors = []
                            this.resolved = true

                            fbqTrack(result)

                            const { redirect_url } = result
                            const { redirect } = this.payload

                            if (!this.payload.hasOwnProperty("redirect") && redirect_url) {
                                this.redirect(redirect_url)

                                return
                            }

                            if (redirect && redirect_url) {
                                this.redirect(redirect_url)
                            } else {
                                this.settings.children[0].isActive = false
                                this.settings.children[1].isActive = true
                                this.$forceUpdate()
                            }
                        })
                        .catch(([message, errors]) => {
                            this.message = message
                            this.errors = errors
                        })
                }
            }

            if (this.target === TARGETS.WEBHOOK || this.payload.enable_wh) {
                const { fields, wh_url, method, headers, mode, body } = this.payload

                formData.append("wh_url", wh_url)
                formData.append("wh_mode", mode)
                formData.append("wh_body", body)
                formData.append("wh_fields", JSON.stringify(fields))
                formData.append("wh_headers", JSON.stringify(headers))
                formData.append("wh_method", method)
            }

            this.loading = true

            fetch(url, {
                method: "POST",
                body: formData,
                credentials: "include"
            })
                .then(response => response.json())
                .then(result => {
                    callback(result)
                })
                .catch(e => {
                    console.log("error", e)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        isCanPaymentProcessing() {
            if (this.payload.offers && this.payload.offers.length) {
                const [offer] = this.payload.offers
                return !offer.is_free && (offer.merchant_settings_count > 1 || offer.payment_type.id === 2)
            } else {
                return false
            }
        },
        isPredefinedProcessing() {
            return this.payload.processing_type && this.payload.processing_type.value === "predifined"
        },
        isOpenProcessing() {
            return this.payload.processing_type && this.payload.processing_type.value === "open"
        },
        isInternalProcessing() {
            return this.payload.processing_type && this.payload.processing_type.value === "internal"
        },
        isCanRedirectToPaymentSystem() {
            return (
                this.payload.offers &&
                this.payload.offers.length &&
                (this.payload.offers[0].merchant_settings_count === 1 ||
                    (this.payload.offers[0].payment_type && !this.payload.offers[0].payment_type.id === 2))
            )
        },
        async injectToken() {
            if (this.getToken) {
                return this.getToken()
            } else {
                const response = await fetch(`${APP_DOMAIN}/cta-settings/${this.uuid}`)
                const result = await response.json()
                return result.cta_token
            }
        },
        async injectAppUrl() {
            const response = await fetch(`${APP_DOMAIN}/cta-settings/${this.uuid}`)
            const result = await response.json()
            return result.app_url
        },
        hide() {
            this.$refs.form.style.overflow = "hidden"
            this.$refs.form.style.height = "0"

            const close = document.querySelector(".widget-close button")

            if (close) {
                close.click()
            }
        }
    },
    computed: {
        loaderInnerStyle() {
            return loaderStyle
        },
        loaderStyle() {
            return {
                position: "absolute",
                zIndex: 55
            }
        },
        messageStyle() {
            return {
                color: "#DD4141",
                fontSize: "12px",
                width: "100%",
                maxWidth: "400px",
                textAlign: "left",
                marginBottom: "15px"
            }
        },
        target() {
            switch (this.payload.id) {
                case 1:
                    return TARGETS.CONTACT
                case 2:
                    return TARGETS.PRODUCT
                case 4:
                    return TARGETS.WEBHOOK
                default:
                    return TARGETS.CONTACT
            }
        },
        desktop() {
            return this.settings.desktop
        },
        mobile() {
            return this.settings.mobile
        },
        general() {
            return this.settings.general
        },
        payload() {
            return this.general.payload
        },
        stepStyle() {
            return {
                opacity: this.loading || this.settings.general.loaderVisible ? 0.4 : 1,
                ...this.resolveStyle(
                    {
                        flexDirection: this.general.isRow ? "row" : "column",
                        alignItems: !this.general.isRow ? this.desktop.justify : this.desktop.align,
                        justifyContent: !this.general.isRow ? this.desktop.align : this.desktop.justify
                    },
                    {
                        flexDirection: "column",
                        alignItems: this.mobile.justify,
                        justifyContent: this.mobile.align
                    }
                )
            }
        },
        blockStyle() {
            return {
                display: "flex",
                height: "max-content",
                flexDirection: "column",
                transition: ".2s",
                minHeight: this.resolved ? `0` : `${this.general.height.value}${this.general.height.type}`,
                ...resolveBackground(this.settings.background),
                ...(this.isBuilderDev ? allEvents() : noneEvents()),
                ...typedWidth(this.general.width),
                ...resolveShadow(this.general.shadow),
                ...this.resolveStyle(
                    {
                        alignItems: this.desktop.justify,
                        justifyContent: this.desktop.align,
                        ...resolveMargin(this.desktop.margin),
                        ...resolvePadding(this.resolved ? [0, 0, 0, 0] : this.desktop.padding)
                    },
                    {
                        alignItems: this.mobile.justify,
                        justifyContent: this.mobile.align,
                        ...resolveMargin(this.mobile.margin),
                        ...resolvePadding(this.resolved ? [0, 0, 0, 0] : this.mobile.padding)
                    }
                )
            }
        }
    },
    watch: {
        settings: {
            deep: true,
            async handler() {
                if (this.isCanPaymentProcessing() && !this.payload.offer_data) {
                    this.loading = true
                    try {
                        await this.checkOfferData()
                    } catch (e) {
                        console.log(e)
                    } finally {
                        this.loading = false
                    }
                }
            }
        }
    }
}
</script>
